import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as api from '../api/api';
import { buildSummaryParams } from '../utils/insights/paramBuilders';
import { canConfirmSummary, exceedsCapMessage } from '../utils/insights/verificationSummary';

/**
 * Hook para gestionar la verificación y generación de resúmenes
 * @param {string} summaryName - Nombre del resumen
 * @param {Object} dateRange - Rango de fechas
 * @param {string[]} selectedJobNames - JobNames seleccionados (persistidos cross-page)
 * @param {number} totalSelectedCount - Total de items seleccionados cross-page
 * @param {Array} tableItems - Items de la tabla (para validación)
 * @param {string|null} selectedSupervisor - Supervisor seleccionado
 * @param {Array} selectedAgents - Agentes seleccionados
 * @param {boolean} allFiltered - Modo «todas las del filtro» (feature 025, US8): se manda sólo el
 *   filtro, sin selectedJobNames, y el backend recorre el rango entero
 */
export function useSummaryGeneration(
  summaryName,
  dateRange,
  selectedJobNames,
  totalSelectedCount,
  tableItems,
  selectedSupervisor,
  selectedAgents,
  allFiltered = false
) {
  const history = useHistory();
  const { t } = useTranslation();
  
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [processParams, setProcessParams] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showPostSuccessModal, setShowPostSuccessModal] = useState(false);
  const [postRequestResult, setPostRequestResult] = useState("");

  const isCreateSummaryButtonEnabled = () => {
    if (!summaryName || summaryName.trim() === "") return false;
    
    const hasDateRange = dateRange !== null && dateRange.startDate && dateRange.endDate;
    const hasSelection = allFiltered || totalSelectedCount > 0;
    const hasTableItems = tableItems.length > 0;
    
    return hasDateRange && hasSelection && hasTableItems && !generatingSummary && !verifying;
  };

  const generateSummary = async () => {
    if (!summaryName || summaryName.trim() === "") return;
    if (!dateRange) return;
    if (!allFiltered && totalSelectedCount === 0) return;

    try {
      setVerifying(true);
      setGeneratingSummary(true);
      
      const baseParams = {};
      // En el modo «todas las del filtro» no viaja ninguna lista (evita el límite de URL, plan D10)
      const filteredJobNames = allFiltered ? [] : selectedJobNames.filter(Boolean);
      const includeSelectedJobNames = !allFiltered;
      
      if (dateRange) {
        baseParams.startDate = dateRange.startDate;
        baseParams.endDate = dateRange.endDate;
      }
      
      if (filteredJobNames.length > 0) {
        baseParams.selectedJobNames = filteredJobNames;
      } else if (!dateRange) {
        return;
      }
      
      const verifyParams = buildSummaryParams(baseParams, {
        summaryName,
        selectedJobNames: filteredJobNames,
        supervisor: selectedSupervisor,
        agents: selectedAgents,
        includeSelectedJobNames
      });
      
      console.log('[useSummaryGeneration] Parámetros enviados a verifyData (GET):', verifyParams);
      const verifyResponse = await api.verifyData(verifyParams);
      
      if (!verifyResponse.found || verifyResponse.count === 0) {
        setPostRequestResult(
          verifyResponse.message ||
          t("insights.noCallsInFilter", { defaultValue: "No se encontraron llamadas que coincidan con los filtros" })
        );
        setShowPostSuccessModal(true);
        return;
      }
      
      // Opción A (revisión del PR, T086): con el máximo por corrida superado no se recorta ni se
      // deja confirmar; se dice por qué, para que se acote el filtro. Vale para los dos modos.
      if (!canConfirmSummary(verifyResponse)) {
        setPostRequestResult(exceedsCapMessage(verifyResponse, t));
        setShowPostSuccessModal(true);
        return;
      }

      setVerificationResult(verifyResponse);
      
      const params = buildSummaryParams(baseParams, {
        summaryName,
        selectedJobNames: filteredJobNames,
        supervisor: selectedSupervisor,
        agents: selectedAgents,
        includeSelectedJobNames
      });
      setProcessParams(params);
      
      setShowConfirmationModal(true);
      
    } catch (error) {
      console.error('Error en verificación:', error);
      setPostRequestResult(error.message || "Error al verificar datos");
      setShowPostSuccessModal(true);
    } finally {
      setVerifying(false);
      setGeneratingSummary(false);
    }
  };

  const processAfterConfirmation = async () => {
    try {
      setGeneratingSummary(true);
      
      console.log('[useSummaryGeneration] Parámetros enviados a postSummary (POST):', processParams);
      const response = await api.postSummary(processParams);

      // feature 025 (US4): el 202 ya es la respuesta completa — la corrida quedó IN_PROGRESS y
      // el aviso llega despues por websocket (T041). No hay nada que esperar antes de navegar.
      if (response && response.message) {
        setPostRequestResult(response.message);
        setShowPostSuccessModal(true);
      }

      history.push('/history-insights-calls');

    } catch (error) {
      console.error('Error generating summary:', error);
      // Mostrar error en modal
      setPostRequestResult(error.message || t("insights.postError"));
      setShowPostSuccessModal(true);
    } finally {
      setGeneratingSummary(false);
      setShowConfirmationModal(false);
    }
  };

  return {
    generatingSummary,
    verifying,
    verificationResult,
    processParams,
    generateSummary,
    processAfterConfirmation,
    isCreateSummaryButtonEnabled,
    showConfirmationModal,
    setShowConfirmationModal,
    showPostSuccessModal,
    setShowPostSuccessModal,
    postRequestResult,
    setPostRequestResult
  };
}

